import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useCreditConfirm } from '../components/credits/useCreditConfirm';
import { CreditCostNotice } from '../components/credits/CreditCostNotice';

const API_BASE_URL = '/api';
const DM_TEXT_LIMIT = 2000;

const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('auth_token');
  const savedTenantId =
    localStorage.getItem('superadmin_selected_tenant') ||
    localStorage.getItem('selected_tenant_id');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  if (savedTenantId) {
    (headers as Record<string, string>)['X-Tenant-Id'] = savedTenantId;
  }
  return fetch(url, { ...options, headers });
};

interface ProfileInfo {
  id: string;
  name: string;
  lateProfileId: string | null;
  connectionCount: number;
}

interface ConnectionInfo {
  id: string;
  platform: string;
  accountName: string | null;
  accountUsername: string | null;
  platformAccountId: string | null;
  lateProfileId: string | null;
  supportsPrivateReply: boolean;
  supportsCommentReply: boolean;
}

interface PostInfo {
  id: string;
  type?: 'post' | 'story';
  content: string;
  permalink: string | null;
  platform: string;
  commentCount: number;
  createdAt: string | null;
  thumbnail: string | null;
}

interface Automation {
  id: string;
  tenantId: string;
  lateConnectionId: string;
  name: string;
  postId: string | null;
  postTitle: string | null;
  triggerKeywords: string[];
  replyType: 'text' | 'openai';
  replyMessage: string | null;
  stats: {
    totalReplies: number;
    totalPrivateReplies: number;
    lastTriggeredAt: string | null;
    publicReplyMessages?: string[];
    injectCaption?: boolean;
    scheduledFor?: string | null;
    pendingPost?: boolean;
    status?: string;
    source?: string;
    // Ultima verificacao de publicacao de uma automacao pendente (por que ainda esta pendente).
    lastCheck?: {
      at?: string;
      attempts?: number;
      outcome?: 'not_published_yet' | 'fetch_failed' | 'no_connection' | 'post_id_unresolved';
      error?: string | null;
      postStatus?: string | null;
      platformStatus?: string | null;
      platformPostUrl?: string | null;
    } | null;
    followerCheck?: {
      enabled?: boolean;
      promptMessage?: string;
      buttonText?: string;
      followerMessage?: string;
      nonFollowerMessage?: string;
    } | null;
    dmButton?: {
      enabled?: boolean;
      type?: 'reply' | 'url';
      text?: string;
      payload?: string;
      url?: string;
    } | null;
    dmButtons?: DmButtonConfig[] | null;
    dmTrigger?: { keywords?: string[]; message?: string; matchMode?: 'exact' | 'contains' } | null;
  };
  privateReplyMessage: string | null;
  openaiModel: string | null;
  openaiSystemPrompt: string | null;
  useKnowledgeBase: boolean;
  injectCaption?: boolean;
  isActive: boolean;
  pending?: boolean;
  processedCommentIds: string[];
  createdAt: string;
  lateConnection?: {
    id: string;
    platform: string;
    accountName: string | null;
    accountUsername: string | null;
  };
}

interface DmButtonConfig {
  id?: string;
  enabled?: boolean;
  type: 'reply' | 'url';
  text: string;
  payload?: string;
  url?: string | null;
}

const createDmButton = (type: 'reply' | 'url' = 'reply'): DmButtonConfig => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  enabled: true,
  type,
  text: type === 'url' ? 'Acessar link' : 'QUERO',
  payload: type === 'url' ? undefined : 'QUERO',
  url: type === 'url' ? '' : null,
});

const PLATFORM_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'Twitter/X',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  reddit: 'Reddit',
  bluesky: 'Bluesky',
  threads: 'Threads',
};

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#E4405F',
  facebook: '#1877F2',
  twitter: '#000000',
  youtube: '#FF0000',
  linkedin: '#0A66C2',
  tiktok: '#000000',
  reddit: '#FF4500',
  bluesky: '#0085FF',
  threads: '#000000',
};

function PlatformBadge({ platform }: { platform: string }) {
  const color = PLATFORM_COLORS[platform] || '#666';
  const name = PLATFORM_NAMES[platform] || platform;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: color }}
    >
      {name}
    </span>
  );
}

export function CommentAutomationPage() {
  const { request: requestCreditConfirm, modal: creditConfirmModal } = useCreditConfirm();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [profiles, setProfiles] = useState<ProfileInfo[]>([]);
  const [connections, setConnections] = useState<ConnectionInfo[]>([]);
  const [posts, setPosts] = useState<PostInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [saving, setSaving] = useState(false);
  const [commentAiSupported, setCommentAiSupported] = useState(false);
  const [runningPollId, setRunningPollId] = useState<string | null>(null);
  const [runningDmPollId, setRunningDmPollId] = useState<string | null>(null);
  const [checkingPublicationId, setCheckingPublicationId] = useState<string | null>(null);
  const [sourceTab, setSourceTab] = useState<'all' | 'crm' | 'marketing'>('all');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [formProfileId, setFormProfileId] = useState('');
  const [formName, setFormName] = useState('');
  const [formConnectionId, setFormConnectionId] = useState('');
  const [formPostId, setFormPostId] = useState('');
  const [formPostTitle, setFormPostTitle] = useState('');
  const [formKeywords, setFormKeywords] = useState('');
  const [formReplyType, setFormReplyType] = useState<'text' | 'openai'>('text');
  const [formReplyMessage, setFormReplyMessage] = useState('');
  const [formReplyMessages, setFormReplyMessages] = useState<string[]>(['']);
  const [formPrivateReplyMessage, setFormPrivateReplyMessage] = useState('');
  // TikTok: a DM so sai quando a pessoa manda a palavra combinada na DM.
  const [formDmTriggerKeywords, setFormDmTriggerKeywords] = useState('');
  const [formDmTriggerMessage, setFormDmTriggerMessage] = useState('');
  const [formDmTriggerMatchMode, setFormDmTriggerMatchMode] = useState<'exact' | 'contains' | 'any'>('exact');
  const [formDmButtonEnabled, setFormDmButtonEnabled] = useState(false);
  const [formDmButtons, setFormDmButtons] = useState<DmButtonConfig[]>([createDmButton()]);
  const [formFollowerCheckEnabled, setFormFollowerCheckEnabled] = useState(false);
  const [formFollowerCheckPromptMessage, setFormFollowerCheckPromptMessage] = useState('Clique no botao abaixo para liberar seu acesso.');
  const [formFollowerCheckButtonText, setFormFollowerCheckButtonText] = useState('CHECAR');
  const [formFollowerCheckFollowerMessage, setFormFollowerCheckFollowerMessage] = useState('Obrigado por seguir. Aqui esta o que prometemos.');
  const [formFollowerCheckNonFollowerMessage, setFormFollowerCheckNonFollowerMessage] = useState('Para receber o material, siga nosso perfil e responda CHECAR novamente.');
  const [formOpenaiSystemPrompt, setFormOpenaiSystemPrompt] = useState('');
  const [formUseKnowledgeBase, setFormUseKnowledgeBase] = useState(false);
  const [formInjectCaption, setFormInjectCaption] = useState(false);

  // AI rewrite modal state
  const [showAiRewrite, setShowAiRewrite] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const selectedConnection = connections.find(c => c.id === formConnectionId);
  const visibleAutomations = automations.filter((auto) => {
    const isMarketing = Boolean(auto.pending || auto.stats?.source === 'marketing_studio');
    if (sourceTab === 'marketing') return isMarketing;
    if (sourceTab === 'crm') return !isMarketing;
    return true;
  });

  const loadAutomations = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) { setAutomations(data.data || []); setCommentAiSupported(data.commentAiVersion === 1); }
      }
    } catch (error) {
      console.error('Erro ao carregar automacoes:', error);
    }
  }, [authenticatedFetch]);

  const loadProfiles = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations/profiles`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) setProfiles(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  }, []);

  const loadConnections = useCallback(async (profileId?: string) => {
    setLoadingConnections(true);
    try {
      const url = profileId
        ? `${API_BASE_URL}/comment-automations/connections?profileId=${profileId}`
        : `${API_BASE_URL}/comment-automations/connections`;
      const res = await authenticatedFetch(url);
      if (res.ok) {
        const data = await res.json();
        if (data.success) setConnections(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar conexoes:', error);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  const loadPosts = useCallback(async (connectionId: string) => {
    if (!connectionId) { setPosts([]); return; }
    setLoadingPosts(true);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations/posts/${connectionId}`);
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) setPosts(data.data || []);
      else {
        // Antes o erro era engolido e a lista só aparecia vazia (ex.: TikTok
        // conectado pelo caminho antigo, que a Zernio recusa).
        setPosts([]);
        toast.error(data?.message || 'Nao foi possivel carregar as postagens dessa conta');
      }
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadAutomations(), loadProfiles(), loadConnections()]);
      setLoading(false);
    };
    init();
  }, [loadAutomations, loadProfiles, loadConnections]);

  // When profile changes in the form, reload connections filtered by that profile
  useEffect(() => {
    if (formProfileId) {
      loadConnections(formProfileId);
    } else {
      loadConnections();
    }
  }, [formProfileId, loadConnections]);

  useEffect(() => {
    if (formConnectionId) {
      loadPosts(formConnectionId);
    } else {
      setPosts([]);
    }
  }, [formConnectionId, loadPosts]);

  const openCreateModal = () => {
    setEditingId(null);
    setFormProfileId('');
    setFormName('');
    setFormConnectionId('');
    setFormPostId('');
    setFormPostTitle('');
    setFormKeywords('');
    setFormReplyType('text');
    setFormReplyMessage('');
    setFormReplyMessages(['']);
    setFormPrivateReplyMessage('');
    setFormDmButtonEnabled(false);
    setFormDmButtons([createDmButton()]);
    setFormFollowerCheckEnabled(false);
    setFormFollowerCheckPromptMessage('Clique no botao abaixo para liberar seu acesso.');
    setFormFollowerCheckButtonText('CHECAR');
    setFormFollowerCheckFollowerMessage('Obrigado por seguir. Aqui esta o que prometemos.');
    setFormFollowerCheckNonFollowerMessage('Para receber o material, siga nosso perfil e responda CHECAR novamente.');
    setFormDmTriggerKeywords('');
    setFormDmTriggerMessage('');
    setFormDmTriggerMatchMode('exact');
    setFormOpenaiSystemPrompt('');
    setFormUseKnowledgeBase(false);
    setFormInjectCaption(false);
    setShowModal(true);
  };

  const openEditModal = (auto: Automation) => {
    setEditingId(auto.id);
    loadAutomationIntoForm(auto, false);
  };

  const openDuplicateModal = (auto: Automation) => {
    setEditingId(null);
    loadAutomationIntoForm(auto, true);
  };

  const loadAutomationIntoForm = (auto: Automation, duplicate: boolean) => {
    // Find the profile for this connection
    const conn = connections.find(c => c.id === auto.lateConnectionId);
    setFormProfileId(conn?.lateProfileId || '');
    setFormName(duplicate ? `${auto.name} (copia)` : auto.name);
    setFormConnectionId(auto.lateConnectionId);
    setFormPostId(auto.postId || '');
    setFormPostTitle(auto.postTitle || '');
    setFormKeywords((auto.triggerKeywords || []).join(', '));
    setFormReplyType(auto.replyType || 'text');
    const publicReplies = Array.isArray(auto.stats?.publicReplyMessages) && auto.stats.publicReplyMessages.length > 0
      ? auto.stats.publicReplyMessages
      : (auto.replyMessage ? [auto.replyMessage] : ['']);
    setFormReplyMessages(publicReplies.length > 0 ? publicReplies : ['']);
    setFormReplyMessage(publicReplies[0] || '');
    setFormPrivateReplyMessage(auto.privateReplyMessage || '');
    setFormDmTriggerKeywords((auto.stats?.dmTrigger?.keywords || []).join(', '));
    setFormDmTriggerMessage(auto.stats?.dmTrigger?.message || '');
    setFormDmTriggerMatchMode(auto.stats?.dmTrigger?.matchMode || 'contains');
    const dmButtons = Array.isArray(auto.stats?.dmButtons) && auto.stats.dmButtons.length > 0
      ? auto.stats.dmButtons
      : (auto.stats?.dmButton?.enabled ? [auto.stats.dmButton as DmButtonConfig] : []);
    setFormDmButtonEnabled(dmButtons.length > 0);
    setFormDmButtons(dmButtons.length > 0 ? dmButtons.map((button) => ({
      id: button.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      enabled: true,
      type: button.type === 'url' ? 'url' : 'reply',
      text: button.text || button.payload || 'QUERO',
      payload: button.payload || button.text || 'QUERO',
      url: button.url || '',
    })).slice(0, 3) : [createDmButton()]);
    const followerCheck = auto.stats?.followerCheck || null;
    setFormFollowerCheckEnabled(Boolean(followerCheck?.enabled));
    setFormFollowerCheckPromptMessage(followerCheck?.promptMessage || 'Clique no botao abaixo para liberar seu acesso.');
    setFormFollowerCheckButtonText(followerCheck?.buttonText || 'CHECAR');
    setFormFollowerCheckFollowerMessage(followerCheck?.followerMessage || 'Obrigado por seguir. Aqui esta o que prometemos.');
    setFormFollowerCheckNonFollowerMessage(followerCheck?.nonFollowerMessage || 'Para receber o material, siga nosso perfil e responda CHECAR novamente.');
    setFormOpenaiSystemPrompt(auto.openaiSystemPrompt || '');
    setFormUseKnowledgeBase(auto.useKnowledgeBase || false);
    setFormInjectCaption((auto.injectCaption ?? auto.stats?.injectCaption) === true);
    setShowModal(true);
  };

  const handleAiRewrite = async () => {
    if (!aiInstruction.trim()) return;
    setAiLoading(true);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/categorias/ai-rewrite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentText: formOpenaiSystemPrompt,
          instruction: aiInstruction.trim(),
          field: 'prompt',
        }),
      });
      const data = await res.json();
      if (data.rewrittenText) {
        setFormOpenaiSystemPrompt(data.rewrittenText);
        toast.success('Prompt reescrito com sucesso pela IA');
        setShowAiRewrite(false);
        setAiInstruction('');
      } else {
        toast.error(data.message || 'Erro ao reescrever');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao reescrever com IA');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Nome e obrigatorio'); return; }
    if (!formConnectionId) { toast.error('Selecione uma conexao'); return; }
    if (!formPostId) { toast.error('Selecione um post ou story'); return; }
    const selectedTarget = posts.find(post => post.id === formPostId);

    if (formFollowerCheckEnabled) {
      if (selectedConnection?.platform !== 'instagram') { toast.error('Checar seguidor exige uma conta Instagram'); return; }
      if (!formFollowerCheckPromptMessage.trim() || !formFollowerCheckButtonText.trim() || !formFollowerCheckFollowerMessage.trim() || !formFollowerCheckNonFollowerMessage.trim()) {
        toast.error('Preencha o texto do botao e as duas mensagens do fluxo de seguidor');
        return;
      }
      if ([formFollowerCheckPromptMessage, formFollowerCheckFollowerMessage, formFollowerCheckNonFollowerMessage].some(msg => msg.length > DM_TEXT_LIMIT)) {
        toast.error(`Mensagens de DM devem ter no maximo ${DM_TEXT_LIMIT} caracteres`);
        return;
      }
    }

    if (!formFollowerCheckEnabled && formPrivateReplyMessage.length > DM_TEXT_LIMIT) {
      toast.error(`Mensagem da DM deve ter no maximo ${DM_TEXT_LIMIT} caracteres`);
      return;
    }

    const normalizedDmButtons = formDmButtonEnabled
      ? formDmButtons.slice(0, 3).map((button) => ({
        enabled: true,
        type: button.type,
        text: button.text.trim(),
        payload: (button.payload || button.text).trim(),
        url: button.type === 'url' ? String(button.url || '').trim() : null,
      })).filter((button) => button.text)
      : [];

    if (!formFollowerCheckEnabled && formDmButtonEnabled) {
      if (normalizedDmButtons.length === 0) {
        toast.error('Informe pelo menos um botao da DM');
        return;
      }
      if (!formPrivateReplyMessage.trim()) {
        toast.error('Para adicionar botao na DM, escreva uma mensagem em "Responder no inbox (DM privada)"');
        return;
      }
      const invalidUrlButton = normalizedDmButtons.find((button) => button.type === 'url' && !/^https?:\/\//i.test(button.url || ''));
      if (invalidUrlButton) {
        toast.error('Informe um link valido com http:// ou https://');
        return;
      }
    }

    const publicReplyMessages = formReplyMessages.map(message => message.trim()).filter(Boolean);
    const usesDmTrigger = selectedConnection?.platform === 'tiktok';
    const dmTriggerMessage = formDmTriggerMessage.trim();
    // Palavra da DM vazia com resposta preenchida: usa as palavras-chave do comentário.
    const dmTriggerAny = formDmTriggerMatchMode === 'any';
    const dmTriggerKeywords = dmTriggerAny ? [] : (formDmTriggerKeywords.trim() || (dmTriggerMessage ? formKeywords : ''))
      .split(',').map(k => k.trim()).filter(Boolean);
    const hasDmTrigger = usesDmTrigger && (dmTriggerAny || dmTriggerKeywords.length > 0) && Boolean(dmTriggerMessage);
    if (usesDmTrigger && !dmTriggerAny && dmTriggerMessage && dmTriggerKeywords.length === 0) {
      toast.error('Preencha a "Palavra que a pessoa manda na DM" (ex.: QUERO)');
      return;
    }
    if (usesDmTrigger && dmTriggerKeywords.length > 0 && !dmTriggerMessage) {
      toast.error('Preencha a "Resposta na DM" ou apague a palavra da DM');
      return;
    }

    if (formReplyType === 'text') {
      if (publicReplyMessages.length === 0 && !formPrivateReplyMessage.trim() && !formFollowerCheckEnabled && !hasDmTrigger) {
        toast.error('Preencha pelo menos uma resposta (publica ou privada)');
        return;
      }
      if (selectedTarget?.type === 'story' && !formPrivateReplyMessage.trim() && !formFollowerCheckEnabled) {
        toast.error('Para story, configure uma resposta no inbox/DM');
        return;
      }
    } else if (formReplyType === 'openai') {
      if (!formOpenaiSystemPrompt.trim()) { toast.error('Prompt do sistema e obrigatorio'); return; }
    }

    if ((formReplyType === 'openai' || formInjectCaption) && !commentAiSupported) {
      toast.error('O backend em execução ainda não confirma suporte à IA de comentários. Atualize o backend junto com o frontend.', { duration: 12000 });
      return;
    }
    setSaving(true);
    try {
      const keywords = formKeywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const body: any = {
        name: formName.trim(),
        lateConnectionId: formConnectionId,
        postId: formPostId || null,
        postTitle: formPostTitle || null,
        triggerKeywords: keywords,
        replyType: formReplyType,
        replyMessage: publicReplyMessages[0] || null,
        replyMessages: publicReplyMessages,
        privateReplyMessage: usesDmTrigger ? null : (formFollowerCheckEnabled ? formFollowerCheckPromptMessage.trim() : (formPrivateReplyMessage.trim() || null)),
        ...(usesDmTrigger ? { dmTrigger: hasDmTrigger ? { keywords: dmTriggerKeywords, message: dmTriggerMessage, matchMode: formDmTriggerMatchMode } : null } : {}),
        openaiModel: 'cx/gpt-6-luna', // modelo do sistema
        openaiSystemPrompt: formOpenaiSystemPrompt.trim() || null,
        useKnowledgeBase: formUseKnowledgeBase,
        injectCaption: formInjectCaption,
        followerCheck: formFollowerCheckEnabled ? {
          enabled: true,
          promptMessage: formFollowerCheckPromptMessage.trim(),
          buttonText: formFollowerCheckButtonText.trim(),
          followerMessage: formFollowerCheckFollowerMessage.trim(),
          nonFollowerMessage: formFollowerCheckNonFollowerMessage.trim(),
        } : null,
        dmButtons: !formFollowerCheckEnabled && formDmButtonEnabled ? normalizedDmButtons : [],
        dmButton: !formFollowerCheckEnabled && formDmButtonEnabled ? normalizedDmButtons[0] : null,
      };

      const url = editingId
        ? `${API_BASE_URL}/comment-automations/${editingId}`
        : `${API_BASE_URL}/comment-automations`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (data.success) {
        // Do not claim success if an older API silently ignored the new AI settings.
        const saved = data.data;
        const savedConfig = saved?.pendingAutomation || saved;
        if ((formReplyType === 'openai' || formInjectCaption) && data.commentAiVersion !== 1) {
          throw new Error('O backend em execução não confirma suporte à IA de comentários. Atualize a imagem do backend junto com o frontend.');
        }
        if (!editingId?.startsWith('pending:') && (savedConfig?.replyType !== formReplyType || (savedConfig?.injectCaption ?? savedConfig?.stats?.injectCaption ?? false) !== formInjectCaption)) {
          throw new Error('O servidor não confirmou o modo de resposta ou a opção de injetar legenda. A configuração não foi salva corretamente.');
        }
        toast.success(editingId ? 'Automacao atualizada!' : 'Automacao criada!');
        setShowModal(false);
        await loadAutomations();
      } else {
        toast.error(data.message || 'Erro ao salvar');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar automacao');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string, confirmed = false) => {
    const auto = automations.find(row => row.id === id);
    if (!confirmed && auto?.replyType === 'openai' && !auto.isActive) {
      requestCreditConfirm({ title: 'Ativar automação com IA', items: [{ featureKey: 'crm_ai_interaction', qty: 1, label: 'resposta de comentário' }], note: 'Cada resposta de comentário com IA consome créditos, como no ChatCRM. Sem saldo, a IA não responde.', confirmLabel: 'Ativar automação', onConfirm: () => { void handleToggle(id, true); } });
      return;
    }
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations/${id}/toggle`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        await loadAutomations();
      } else {
        toast.error(data.message || 'Erro');
      }
    } catch (error) {
      toast.error('Erro ao alternar automacao');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta automacao?')) return;
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Automacao removida');
        await loadAutomations();
      } else {
        toast.error(data.message || 'Erro');
      }
    } catch (error) {
      toast.error('Erro ao remover automacao');
    }
  };

  // Automacao presa em "Aguardando publicacao": verifica na hora se o post ja saiu, em vez
  // de depender do scheduler de 2 minutos — e diz o motivo quando continua pendente.
  const handleCheckPublication = async (auto: Automation) => {
    setCheckingPublicationId(auto.id);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations/${auto.id}/check-publication`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.success) toast.success(data.message || 'Verificacao concluida');
      else toast.error(data.message || 'Nao foi possivel verificar a publicacao');
      await loadAutomations();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao verificar publicacao');
    } finally {
      setCheckingPublicationId(null);
    }
  };

  const handleRunPoll = async (auto: Automation) => {
    if (!auto.postId) { toast.error('Essa acao exige uma automacao vinculada a um post especifico.'); return; }
    setRunningPollId(auto.id);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations/${auto.id}/run-poll`, {
        method: 'POST',
        body: JSON.stringify({ limit: 100, maxPages: 5 }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.data?.errors?.length) toast.error(`${data.message} Primeiro erro: ${data.data.errors[0].error}`, { duration: 12000 });
        else toast.success(data.message || 'Comentarios antigos processados', { duration: 9000 });
        await loadAutomations();
      } else {
        toast.error(data.message || 'Erro ao rodar comentarios');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao rodar comentarios');
    } finally {
      setRunningPollId(null);
    }
  };

  const handleRunDmPoll = async (auto: Automation) => {
    if (!auto.postId) { toast.error('Essa acao exige uma automacao vinculada a um post especifico.'); return; }
    if (!auto.privateReplyMessage) { toast.error('Configure uma mensagem em "Responder no inbox (DM privada)" antes de rodar DMs.'); return; }
    setRunningDmPollId(auto.id);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/comment-automations/${auto.id}/run-poll`, {
        method: 'POST',
        body: JSON.stringify({ limit: 100, maxPages: 5, dmOnly: true }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.data?.errors?.length) toast.error(`${data.message} Primeiro erro: ${data.data.errors[0].error}`, { duration: 12000 });
        else toast.success(data.message || 'DMs processadas', { duration: 9000 });
        await loadAutomations();
      } else {
        toast.error(data.message || 'Erro ao rodar DMs');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao rodar DMs');
    } finally {
      setRunningDmPollId(null);
    }
  };

  const updateDmButton = (index: number, changes: Partial<DmButtonConfig>) => {
    setFormDmButtons((current) => current.map((button, itemIndex) => (
      itemIndex === index ? { ...button, ...changes } : button
    )));
  };

  const addDmButton = () => {
    setFormDmButtons((current) => current.length >= 3 ? current : [...current, createDmButton('url')]);
  };

  const removeDmButton = (index: number) => {
    setFormDmButtons((current) => {
      const next = current.filter((_, itemIndex) => itemIndex !== index);
      return next.length > 0 ? next : [createDmButton()];
    });
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-500">Carregando automacoes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Automacao de Comentarios
          </h1>
          <p className="text-gray-500 mt-1">
            Responda automaticamente a comentarios nas suas APIs oficiais
          </p>
        </div>
        <button
          onClick={openCreateModal}
          disabled={connections.length === 0}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nova Automacao
        </button>
      </div>

      {connections.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-amber-800">Nenhuma conexao com suporte a comentarios</p>
              <p className="text-sm text-amber-600 mt-1">
                Conecte uma API oficial (Instagram, Facebook, Twitter, YouTube, etc.) na pagina de <a href="/redes-sociais" className="underline font-medium">APIs Oficiais</a> primeiro.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Automations List */}
      <div className="mb-4 flex flex-wrap gap-2 rounded-2xl border border-gray-200 bg-white p-2">
        {[
          { key: 'all', label: 'Todas', count: automations.length },
          { key: 'crm', label: 'Criadas no CRM', count: automations.filter(a => !a.pending && a.stats?.source !== 'marketing_studio').length },
          { key: 'marketing', label: 'Marketing Studio', count: automations.filter(a => a.pending || a.stats?.source === 'marketing_studio').length },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSourceTab(tab.key as any)}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${sourceTab === tab.key ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            {tab.label} <span className="opacity-75">({tab.count})</span>
          </button>
        ))}
      </div>

      {visibleAutomations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhuma automacao criada</h3>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            Crie automacoes para responder automaticamente a comentarios nos seus posts. Defina palavras-chave, respostas publicas e mensagens privadas.
          </p>
          {connections.length > 0 && (
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Criar primeira automacao
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {visibleAutomations.map(auto => (
            <div
              key={auto.id}
              className={`bg-white rounded-2xl border transition-all ${auto.isActive ? 'border-purple-200 shadow-sm' : 'border-gray-200 opacity-70'}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{auto.name}</h3>
                      <PlatformBadge platform={auto.lateConnection?.platform || ''} />
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${auto.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {auto.isActive ? 'Ativa Ativa' : 'Inativa Inativa'}
                      </span>
                      {auto.pending && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          Aguardando publicacao
                        </span>
                      )}
                      {auto.pending && auto.stats?.lastCheck?.outcome && auto.stats.lastCheck.outcome !== 'not_published_yet' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Verificacao travada
                        </span>
                      )}
                      {!auto.pending && auto.stats?.source === 'marketing_studio' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                          Marketing Studio
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <span>@{auto.lateConnection?.accountUsername || auto.lateConnection?.accountName || '-'}</span>
                      {auto.postTitle && (
                        <>
                          <span>-</span>
                          <span className="truncate max-w-xs" title={auto.postTitle}>{auto.postTitle}</span>
                        </>
                      )}
                      {!auto.postId && <span className="text-purple-600 font-medium">Todos os posts</span>}
                    </div>

                    {/* Por que essa automacao continua aguardando publicacao */}
                    {auto.pending && (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {(() => {
                          const check = auto.stats?.lastCheck;
                          const when = check?.at ? new Date(check.at).toLocaleString('pt-BR') : null;
                          const suffix = when ? ` (ultima checagem ${when}${check?.attempts ? `, ${check.attempts}a tentativa` : ''})` : '';
                          if (!check) {
                            return <>A automacao sera ativada sozinha quando a postagem publicar. A verificacao roda a cada 2 minutos.</>;
                          }
                          if (check.outcome === 'not_published_yet') {
                            return <>A postagem ainda nao publicou{check.postStatus ? ` — status na Zernio: ${check.postStatus}` : ''}{check.platformStatus ? ` / plataforma: ${check.platformStatus}` : ''}.{suffix}</>;
                          }
                          if (check.outcome === 'fetch_failed') {
                            return <><strong>Nao foi possivel consultar a postagem na Zernio.</strong> {check.error}{suffix}</>;
                          }
                          if (check.outcome === 'no_connection') {
                            return <><strong>Conexao da conta nao encontrada.</strong> {check.error} Reconecte a conta ou escolha outra na edicao.{suffix}</>;
                          }
                          if (check.outcome === 'post_id_unresolved') {
                            return <><strong>A postagem publicou, mas nao foi identificada automaticamente.</strong> Abra a edicao e escolha a publicacao na lista para ativar agora.{check.platformPostUrl ? <> <a href={check.platformPostUrl} target="_blank" rel="noreferrer" className="underline">Ver publicacao</a></> : null}{suffix}</>;
                          }
                          return <>{check.error || 'Verificacao pendente.'}{suffix}</>;
                        })()}
                      </div>
                    )}

                    {/* Rules summary */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {auto.triggerKeywords.length > 0 ? (
                        <div className="flex items-center gap-1 text-sm bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          Contem: {auto.triggerKeywords.slice(0, 3).map(k => `"${k}"`).join(', ')}
                          {auto.triggerKeywords.length > 3 && ` +${auto.triggerKeywords.length - 3}`}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-sm bg-gray-50 text-gray-600 px-2.5 py-1 rounded-lg">
                          Qualquer comentario
                        </div>
                      )}

                      {(auto.replyType || 'text') === 'openai' ? (
                        <div className="flex items-center gap-1 text-sm bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg">
                          Resposta com IA
                        </div>
                      ) : auto.replyMessage ? (
                        <div className="flex items-center gap-1 text-sm bg-green-50 text-green-700 px-2.5 py-1 rounded-lg">
                          Resposta publica
                        </div>
                      ) : null}

                      {auto.useKnowledgeBase && (
                        <div className="flex items-center gap-1 text-sm bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-lg">
                          Base de conhecimento
                        </div>
                      )}

                      {auto.privateReplyMessage && (
                        <div className="flex items-center gap-1 text-sm bg-orange-50 text-orange-700 px-2.5 py-1 rounded-lg">
                          Resposta no inbox
                        </div>
                      )}
                      {auto.stats?.dmTrigger?.keywords?.length ? (
                        <div className="flex items-center gap-1 text-sm bg-orange-50 text-orange-700 px-2.5 py-1 rounded-lg">
                          DM ao mandar "{auto.stats.dmTrigger.keywords.join(', ')}"
                        </div>
                      ) : null}
                    </div>

                    {auto.stats?.followerCheck?.enabled && (
                      <div className="mb-3 inline-flex items-center gap-1 text-sm bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-lg">
                        Checar seguidor
                      </div>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>{auto.stats?.totalReplies || 0} respostas</span>
                      {Boolean(auto.privateReplyMessage || auto.stats?.dmTrigger?.keywords?.length) && <span>{auto.stats?.totalPrivateReplies || 0} DMs</span>}
                      {auto.pending && auto.stats?.scheduledFor && (
                        <span>Publica em: {new Date(auto.stats.scheduledFor).toLocaleString('pt-BR')}</span>
                      )}
                      {auto.stats?.lastTriggeredAt && (
                        <span>Ultimo disparo: {new Date(auto.stats.lastTriggeredAt).toLocaleString('pt-BR')}</span>
                      )}
                      <span>{auto.processedCommentIds?.length || 0} comentarios processados</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <div className="flex flex-col gap-1.5">
                      {auto.pending && (
                        <button
                          onClick={() => handleCheckPublication(auto)}
                          disabled={checkingPublicationId === auto.id}
                          className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Verificar agora se a postagem publicou e ativar a automacao"
                        >
                          {checkingPublicationId === auto.id ? 'Verificando...' : 'Verificar publicacao'}
                        </button>
                      )}
                      <button
                        onClick={() => handleRunPoll(auto)}
                        disabled={auto.pending || !auto.postId || runningPollId === auto.id || runningDmPollId === auto.id}
                        className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Buscar comentarios antigos deste post e executar esta automacao"
                      >
                        {runningPollId === auto.id ? 'Rodando...' : 'Rodar comentarios'}
                      </button>
                      <button
                        onClick={() => handleRunDmPoll(auto)}
                        disabled={auto.pending || !auto.postId || !auto.privateReplyMessage || runningPollId === auto.id || runningDmPollId === auto.id}
                        className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Enviar DM para todos os comentarios deste post que ainda nao receberam DM"
                      >
                        {runningDmPollId === auto.id ? 'Rodando...' : 'Rodar DMs'}
                      </button>
                    </div>
                    <button
                      onClick={() => handleToggle(auto.id)}
                      disabled={auto.pending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${auto.isActive ? 'bg-purple-600' : 'bg-gray-300'}`}
                      title={auto.isActive ? 'Desativar' : 'Ativar'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${auto.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <button
                      onClick={() => openEditModal(auto)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => openDuplicateModal(auto)}
                      disabled={auto.pending}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Duplicar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 8h10a2 2 0 012 2v10a2 2 0 01-2 2H8a2 2 0 01-2-2V10a2 2 0 012-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16c-1.1 0-2-.9-2-2V4a2 2 0 012-2h10c1.1 0 2 .9 2 2" />
                      </svg>
                      Duplicar
                    </button>
                    <button
                      onClick={() => handleDelete(auto.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info box */}
      <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4">
        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Como funciona
        </h4>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>- As automacoes verificam novos comentarios <strong>a cada 1 minuto</strong></li>
          <li>- <strong>Resposta publica:</strong> responde diretamente no comentario (todas as plataformas)</li>
          <li>- <strong>Resposta no inbox:</strong> envia DM privada ao autor do comentario (apenas Instagram e Facebook)</li>
          <li>- <strong>Palavras-chave:</strong> se definidas, so dispara quando o comentario contem alguma delas</li>
          <li>- Cada comentario e processado apenas uma vez (sem duplicatas)</li>
        </ul>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Editar Automacao' : 'Nova Automacao de Comentarios'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Configure as regras de resposta automatica</p>
            </div>

            <div className="p-6 space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome da automacao *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="Ex: Responder quem comenta 'preco'"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                />
              </div>

              {/* Group (Profile) */}
              {profiles.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grupo *</label>
                  <p className="text-xs text-gray-400 mb-2">Selecione o grupo para filtrar as conexoes disponiveis</p>
                  <select
                    value={formProfileId}
                    onChange={e => {
                      setFormProfileId(e.target.value);
                      setFormConnectionId('');
                      setFormPostId('');
                      setFormPostTitle('');
                      setFormPrivateReplyMessage('');
                      setFormFollowerCheckEnabled(false);
                    }}
                    disabled={!!editingId}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors disabled:bg-gray-100"
                  >
                    <option value="">Selecione um grupo...</option>
                    {profiles.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.connectionCount} {p.connectionCount === 1 ? 'conexao' : 'conexoes'})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Connection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conexao (rede social) *</label>
                {loadingConnections ? (
                  <div className="flex items-center gap-2 py-3 text-gray-400">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                    Carregando conexoes...
                  </div>
                ) : (
                  <select
                    value={formConnectionId}
                    onChange={e => {
                      setFormConnectionId(e.target.value);
                      setFormPostId('');
                      setFormPostTitle('');
                      setFormPrivateReplyMessage('');
                      setFormFollowerCheckEnabled(false);
                    }}
                    disabled={!!editingId || (profiles.length > 0 && !formProfileId)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors disabled:bg-gray-100"
                  >
                    <option value="">{profiles.length > 0 && !formProfileId ? 'Selecione um grupo primeiro...' : 'Selecione uma conexao...'}</option>
                    {connections.map(c => (
                      <option key={c.id} value={c.id}>
                        {PLATFORM_NAMES[c.platform] || c.platform} - @{c.accountUsername || c.accountName || c.id}
                        {c.supportsPrivateReply ? ' (suporta DM)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Post/story selection */}
              {formConnectionId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Post ou story *</label>
                  <p className="text-xs text-gray-400 mb-2">Escolha exatamente onde essa automacao deve disparar.</p>
                  {editingId?.startsWith('pending:') && (
                    <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Essa automacao esta aguardando publicacao. Se a postagem ja saiu, escolha-a na lista abaixo e salve: a automacao e ativada na hora, vinculada a essa publicacao.
                    </p>
                  )}
                  <p className="text-xs text-purple-600 mb-2">Obrigatorio: escolha um post ou story ativo.</p>
                  {posts.find(post => post.id === formPostId)?.type === 'story' && (
                    <p className="text-xs text-amber-600 mb-2">Story dispara por resposta no inbox/DM. Resposta publica so sera usada se o provedor enviar evento de comentario publico do story.</p>
                  )}
                  {loadingPosts ? (
                    <div className="flex items-center gap-2 py-3 text-gray-400">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                      Carregando posts...
                    </div>
                  ) : posts.length === 0 ? (
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                      Nenhum post ou story ativo encontrado para essa conexao.
                    </div>
                  ) : (
                    <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {posts.map(p => {
                          const isSelected = formPostId === p.id;
                          const isStory = p.type === 'story';
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                setFormPostId(p.id);
                                setFormPostTitle(p.content || '');
                              }}
                              className={`flex flex-col rounded-lg border p-2 text-left transition-colors ${isSelected ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-100' : 'border-gray-200 hover:border-purple-200 hover:bg-gray-50'}`}
                            >
                              <div className={`w-full overflow-hidden rounded-md bg-gray-100 ${isStory ? 'aspect-[9/16]' : 'aspect-square'}`}>
                                {p.thumbnail ? (
                                  <img src={p.thumbnail} alt="" className="h-full w-full object-cover" loading="lazy" />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-gray-400">
                                    {isStory ? 'Story' : 'Post'}
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1 py-2">
                                <div className="mb-1 flex items-center gap-2">
                                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${isStory ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {isStory ? 'Story' : 'Post'}
                                  </span>
                                  {isSelected && <span className="text-[11px] font-semibold text-purple-700">Selecionado</span>}
                                </div>
                                <p className="line-clamp-2 text-sm font-medium text-gray-800">
                                  {p.content || (isStory ? 'Story ativo' : 'Post sem texto')}
                                </p>
                                <p className="mt-1 text-xs text-gray-400">
                                  {p.createdAt ? new Date(p.createdAt).toLocaleString('pt-BR') : 'Sem data'}
                                  {!isStory ? ` - ${p.commentCount || 0} comentarios` : ''}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Trigger keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Palavras-chave (opcional)
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Separe por virgula. Se vazio, responde a qualquer comentario.
                </p>
                <input
                  type="text"
                  value={formKeywords}
                  onChange={e => setFormKeywords(e.target.value)}
                  placeholder="Ex: preco, valor, quanto custa, link"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                />
                {formKeywords && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formKeywords.split(',').map(k => k.trim()).filter(k => k).map((k, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        "{k}"
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Tipo de resposta
                </h3>

                {/* Reply type selector */}
                <div className="flex gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setFormReplyType('text')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-medium transition-all ${formReplyType === 'text' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                  >
                    Texto
                  </button>
                  <button
                    type="button"
                    onClick={() => { if (formReplyType === 'openai') return; requestCreditConfirm({ title: 'Ativar IA nos comentários', items: [{ featureKey: 'crm_ai_interaction', qty: 1, label: 'resposta de comentário' }], note: 'Cada resposta de comentário gerada pela IA consome créditos, com o mesmo valor das respostas do ChatCRM. Sem saldo, a IA não responde. As etapas de DM e checagem de seguidor continuam conforme configuradas.', confirmLabel: 'Ativar IA', onConfirm: () => setFormReplyType('openai') }); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-medium transition-all ${formReplyType === 'openai' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                  >
                    Responder com IA
                  </button>
                </div>

                <div className="space-y-4">
                    {formReplyType === 'text' && <>
                    {/* Public reply */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Responder no comentario (publico)
                      </label>
                      <textarea
                        value={formReplyMessages[0] || ''}
                        onChange={e => setFormReplyMessages(prev => prev.map((item, index) => index === 0 ? e.target.value : item))}
                        placeholder="Ex: Obrigado pelo comentario, {{nome_comentario}}! Enviamos mais informacoes no seu inbox "
                        rows={3}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                      />
                      {formReplyMessages.slice(1).map((message, index) => {
                        const realIndex = index + 1;
                        return (
                          <div key={realIndex} className="mt-2 rounded-xl border border-gray-200 bg-white p-2">
                            <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                              <span>Variacao {realIndex + 1}</span>
                              <button type="button" onClick={() => setFormReplyMessages(prev => prev.filter((_, itemIndex) => itemIndex !== realIndex))} className="font-semibold text-red-600 hover:text-red-700">Remover</button>
                            </div>
                            <textarea
                              value={message}
                              onChange={e => setFormReplyMessages(prev => prev.map((item, itemIndex) => itemIndex === realIndex ? e.target.value : item))}
                              placeholder="Outra resposta possivel para randomizar"
                              rows={3}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                            />
                          </div>
                        );
                      })}
                      <button type="button" onClick={() => setFormReplyMessages(prev => [...prev, ''])} className="mt-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100">+ resposta</button>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <span className="text-xs text-gray-500">Variaveis:</span>
                        {['{{nome_comentario}}', '{{@comentario}}'].map((variable) => (
                          <button
                            key={variable}
                            type="button"
                            onClick={() => setFormReplyMessages(prev => prev.map((item, index) => index === 0 ? item + variable : item))}
                            className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded hover:bg-purple-200"
                          >
                            {variable}
                          </button>
                        ))}
                        <span className="text-xs text-gray-400 ml-1">nome ou @ de quem comentou</span>
                      </div>
                    </div>

                    </>}
                    {/* Private reply */}
                    {!formFollowerCheckEnabled && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                        Responder no inbox (DM privada)
                        {selectedConnection && !selectedConnection.supportsPrivateReply && selectedConnection.platform !== 'tiktok' && (
                          <span className="text-xs text-red-500 font-normal">
                            - Nao disponivel para {PLATFORM_NAMES[selectedConnection.platform] || selectedConnection.platform}
                          </span>
                        )}
                      </label>
                      {(!selectedConnection || selectedConnection.supportsPrivateReply) ? (
                        <>
                          <textarea
                            value={formPrivateReplyMessage}
                            onChange={e => setFormPrivateReplyMessage(e.target.value)}
                            maxLength={DM_TEXT_LIMIT}
                            placeholder="Ex: Ola {{nome_comentario}}! Vi que voce comentou no nosso post. Aqui esta o link: ..."
                            rows={3}
                            disabled={!selectedConnection?.supportsPrivateReply}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none disabled:bg-gray-50 disabled:text-gray-400"
                          />
                          <div className="flex flex-wrap gap-1 mt-2">
                            <span className="text-xs text-gray-500">Variaveis:</span>
                            {['{{nome_comentario}}', '{{@comentario}}'].map((variable) => (
                              <button
                                key={variable}
                                type="button"
                                onClick={() => setFormPrivateReplyMessage(prev => prev + variable)}
                                className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs rounded hover:bg-orange-200"
                              >
                                {variable}
                              </button>
                            ))}
                          </div>
                          <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50 p-3">
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formDmButtonEnabled}
                                onChange={e => setFormDmButtonEnabled(e.target.checked)}
                                className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                              />
                              <span>
                                <span className="block text-sm font-semibold text-orange-800">Adicionar botao na DM</span>
                                <span className="block text-xs text-orange-700 mt-0.5">Envia a resposta escrita junto com um botao clicavel no inbox.</span>
                              </span>
                            </label>
                            {formDmButtonEnabled && (
                              <div className="mt-3 space-y-3">
                                {formDmButtons.map((button, index) => (
                                  <div key={button.id || index} className="rounded-lg border border-orange-200 bg-white p-3">
                                    <div className="mb-3 flex items-center justify-between gap-2">
                                      <span className="text-xs font-semibold text-orange-900">Botao {index + 1}</span>
                                      {formDmButtons.length > 1 && (
                                        <button type="button" onClick={() => removeDmButton(index)} className="text-xs font-semibold text-red-600 hover:text-red-700">
                                          Remover
                                        </button>
                                      )}
                                    </div>
                                    <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg bg-orange-50 p-1">
                                      <button type="button" onClick={() => updateDmButton(index, { type: 'reply', url: null })} className={`rounded-md px-3 py-2 text-xs font-semibold transition-colors ${button.type === 'reply' ? 'bg-orange-600 text-white' : 'text-orange-800 hover:bg-orange-100'}`}>
                                        Resposta
                                      </button>
                                      <button type="button" onClick={() => updateDmButton(index, { type: 'url', payload: undefined })} className={`rounded-md px-3 py-2 text-xs font-semibold transition-colors ${button.type === 'url' ? 'bg-orange-600 text-white' : 'text-orange-800 hover:bg-orange-100'}`}>
                                        Link
                                      </button>
                                    </div>
                                    <label className="block text-xs font-medium text-orange-900 mb-1">Texto do botao</label>
                                    <input value={button.text} onChange={e => updateDmButton(index, { text: e.target.value.slice(0, 20), payload: e.target.value.slice(0, 20) })} maxLength={20} placeholder={button.type === 'url' ? 'Ex: Acessar link' : 'Ex: QUERO'} className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                                    {button.type === 'url' && (
                                      <div className="mt-3">
                                        <label className="block text-xs font-medium text-orange-900 mb-1">Link de redirecionamento</label>
                                        <input value={button.url || ''} onChange={e => updateDmButton(index, { url: e.target.value })} placeholder="https://seusite.com/oferta" className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {formDmButtons.length < 3 && (
                                  <button type="button" onClick={addDmButton} className="w-full rounded-lg border border-dashed border-orange-300 px-3 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-100">
                                    Adicionar outro botao
                                  </button>
                                )}
                                <p className="text-xs text-orange-700">Aceita ate 3 botoes. Link abre uma URL; Resposta envia uma resposta rapida de volta.</p>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Disponivel apenas para Instagram e Facebook. Limite: 1 DM por comentario, dentro de 7 dias, apenas texto.
                          </p>
                        </>
                      ) : selectedConnection.platform === 'tiktok' ? (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3">
                          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            <p className="font-semibold">No TikTok a DM NAO sai sozinha depois do comentario.</p>
                            <p className="mt-1">O TikTok proibe a empresa de mandar a primeira mensagem. A resposta na DM so e enviada depois que a pessoa escrever pra voce na DM:</p>
                            <ol className="mt-2 list-decimal space-y-0.5 pl-5">
                              <li>A pessoa comenta no video.</li>
                              <li>A resposta publica pede pra ela mandar {formDmTriggerMatchMode === 'any' ? 'uma mensagem' : 'a palavra'} na sua DM.</li>
                              <li>Ela abre a DM e manda a mensagem.</li>
                              <li>So entao a automacao responde na DM.</li>
                            </ol>
                            <p className="mt-2">Por isso a resposta publica precisa convidar a pessoa pra DM. Se ela nao mandar mensagem, a DM nunca e enviada.</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Quando responder na DM</label>
                            <select value={formDmTriggerMatchMode} onChange={e => setFormDmTriggerMatchMode(e.target.value as 'exact' | 'contains' | 'any')} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                              <option value="any">Qualquer mensagem (sem palavra, 1 resposta por conversa)</option>
                              <option value="exact">Mensagem igual à palavra/frase</option>
                              <option value="contains">Mensagem contém a palavra/frase</option>
                            </select>
                            {formDmTriggerMatchMode !== 'any' && (
                              <input
                                value={formDmTriggerKeywords}
                                onChange={e => setFormDmTriggerKeywords(e.target.value)}
                                placeholder="Palavra que a pessoa manda na DM. Ex: QUERO (separe varias por virgula)"
                                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Resposta na DM</label>
                            <textarea
                              value={formDmTriggerMessage}
                              onChange={e => setFormDmTriggerMessage(e.target.value)}
                              maxLength={6000}
                              rows={3}
                              placeholder="Ex: Aqui esta o link que prometi: https://..."
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                            />
                          </div>
                          {formDmTriggerMatchMode === 'any' && (
                            <button
                              type="button"
                              onClick={() => {
                                const text = 'Me chama na DM que eu te passo tudo por la!';
                                setFormReplyMessages(prev => (prev.length === 1 && !prev[0].trim()) ? [text] : [...prev, text]);
                              }}
                              className="text-xs font-semibold text-purple-700 hover:text-purple-800"
                            >
                              + Usar "Me chama na DM que eu te passo tudo por la!" como resposta publica
                            </button>
                          )}
                          {formDmTriggerMatchMode !== 'any' && formDmTriggerKeywords.split(',')[0]?.trim() && (
                            <button
                              type="button"
                              onClick={() => {
                                const word = formDmTriggerKeywords.split(',')[0].trim();
                                const text = `Me manda ${word} na DM que eu te falo!`;
                                setFormReplyMessages(prev => (prev.length === 1 && !prev[0].trim()) ? [text] : [...prev, text]);
                              }}
                              className="text-xs font-semibold text-purple-700 hover:text-purple-800"
                            >
                              + Usar "Me manda {formDmTriggerKeywords.split(',')[0].trim()} na DM que eu te falo!" como resposta publica
                            </button>
                          )}
                          <p className="text-xs text-gray-400">
                            {formDmTriggerMatchMode === 'any'
                              ? 'Responde a primeira mensagem de cada pessoa na DM dessa conta (uma vez por conversa). Uma automacao com palavra especifica tem prioridade.'
                              : 'Vale para qualquer pessoa que mandar a palavra na DM dessa conta.'} O TikTok aceita ate 10 mensagens em 48h depois da ultima mensagem da pessoa.
                          </p>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-500">
                          A plataforma <strong>{PLATFORM_NAMES[selectedConnection.platform]}</strong> nao suporta envio de DM a partir de comentarios.
                          Apenas <strong>Instagram</strong> e <strong>Facebook</strong> possuem essa funcionalidade.
                        </div>
                      )}
                    </div>}

                    {selectedConnection?.platform === 'instagram' && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formFollowerCheckEnabled}
                            onChange={e => setFormFollowerCheckEnabled(e.target.checked)}
                            className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                          />
                          <span>
                            <span className="block text-sm font-semibold text-emerald-800">Checar se e seguidor</span>
                            <span className="block text-xs text-emerald-700 mt-0.5">Envia uma DM inicial com botao; quando a pessoa responde, checa se segue e envia uma mensagem diferente.</span>
                          </span>
                        </label>
                        {formFollowerCheckEnabled && (
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-emerald-900 mb-1">Mensagem que acompanha o botao</label>
                              <textarea value={formFollowerCheckPromptMessage} onChange={e => setFormFollowerCheckPromptMessage(e.target.value)} maxLength={DM_TEXT_LIMIT} rows={2} className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-emerald-900 mb-1">Texto do botao</label>
                              <input value={formFollowerCheckButtonText} onChange={e => setFormFollowerCheckButtonText(e.target.value.slice(0, 20))} maxLength={20} className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-emerald-900 mb-1">Mensagem para quem segue</label>
                              <textarea value={formFollowerCheckFollowerMessage} onChange={e => setFormFollowerCheckFollowerMessage(e.target.value)} maxLength={DM_TEXT_LIMIT} rows={2} className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-emerald-900 mb-1">Mensagem para quem nao segue</label>
                              <textarea value={formFollowerCheckNonFollowerMessage} onChange={e => setFormFollowerCheckNonFollowerMessage(e.target.value)} maxLength={DM_TEXT_LIMIT} rows={2} className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                              <p className="mt-1 text-xs text-emerald-700">O mesmo botao inicial sera reenviado junto para a pessoa tentar novamente.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                {formReplyType === 'openai' && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-blue-600 font-medium">Agente de comentários</span>
                      </div>
                      <p className="text-sm text-blue-600">
                        A IA ira gerar respostas automaticamente. O comentario do usuario sera usado como prompt do usuario.
                      </p>
                    </div>

                    <CreditCostNotice items={[{ featureKey: 'crm_ai_interaction', qty: 1, label: 'resposta de comentário' }]} perUnit note="Cada resposta de comentário com IA consome créditos, como no ChatCRM. Sem saldo, a IA não responde." />
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={formInjectCaption} onChange={e => setFormInjectCaption(e.target.checked)} />
                      Injetar a legenda da publicação no prompt
                    </label>

                    {/* System Prompt with "Editar com IA" */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">Prompt do Sistema *</label>
                        <button
                          type="button"
                          onClick={() => { setShowAiRewrite(true); setAiInstruction(''); }}
                          className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                        >
                          Editar com IA
                        </button>
                      </div>
                      <textarea
                        value={formOpenaiSystemPrompt}
                        onChange={e => setFormOpenaiSystemPrompt(e.target.value)}
                        placeholder="Ex: Voce e um assistente virtual da loja X. Responda comentarios de forma profissional e amigavel. Sempre convide o cliente a enviar uma DM para mais informacoes..."
                        rows={4}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      />
                      <div className="flex flex-wrap gap-1 mt-2">
                        <span className="text-xs text-gray-500">Variaveis:</span>
                        {['{{nome_comentario}}', '{{@comentario}}'].map((variable) => (
                          <button
                            key={variable}
                            type="button"
                            onClick={() => setFormOpenaiSystemPrompt(prev => prev + variable)}
                            className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                          >
                            {variable}
                          </button>
                        ))}
                        <span className="text-xs text-gray-400 ml-1">nome ou @ de quem comentou</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Define como a IA deve se comportar. O comentario do usuario sera enviado automaticamente como prompt do usuario.
                      </p>
                    </div>

                    {/* Use Knowledge Base */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formUseKnowledgeBase}
                          onChange={e => setFormUseKnowledgeBase(e.target.checked)}
                          className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Usar Base de Conhecimento</span>
                          <p className="text-xs text-gray-500 mt-1">
                            O agente tera acesso a base vetorial (Vector Store) cadastrada para este tenant. 
                            Ele consultara os documentos enviados para fundamentar suas respostas com informacoes precisas.
                          </p>
                        </div>
                      </label>
                    </div>

                  </div>
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="p-6 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                {editingId ? 'Salvar alteracoes' : 'Criar automacao'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Rewrite Modal */}
      {showAiRewrite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60]" onClick={() => { setShowAiRewrite(false); setAiInstruction(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 border border-gray-100" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">
                Editar Prompt do Sistema com IA
              </h3>
              <button
                type="button"
                onClick={() => { setShowAiRewrite(false); setAiInstruction(''); }}
                className="text-sm text-gray-500 hover:text-gray-700"
                disabled={aiLoading}
              >
                Fechar
              </button>
            </div>
            <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Texto atual</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                {formOpenaiSystemPrompt || '(vazio)'}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                O que deseja alterar?
              </label>
              <textarea
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                rows={4}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                placeholder="Ex: Adicione instrucoes para responder com emojis, mude o tom para mais informal, inclua informacoes sobre promocoes..."
                disabled={aiLoading}
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowAiRewrite(false); setAiInstruction(''); }}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 border border-gray-200"
                disabled={aiLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAiRewrite}
                disabled={aiLoading || !aiInstruction.trim()}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {aiLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Reescrevendo...
                  </>
                ) : (
                  'Reescrever com IA'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {creditConfirmModal}
    </div>
  );
}
